import { useState, useEffect } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Shield, Users, ShoppingCart, CreditCard, Warehouse, Plus, Lock, Eye, Copy, ChevronDown, ChevronRight, Factory, Truck, UserCheck, Edit2 } from "lucide-react";

const DEPT_ICONS = { Sales: ShoppingCart, Finance: CreditCard, Warehouse: Warehouse, Leadership: Shield, Logistics: Truck, Production: Factory, Procurement: ShoppingCart, HR: UserCheck, Support: Users, Other: Users };
const DEPT_COLORS = { Sales: "bg-blue-600", Finance: "bg-violet-600", Warehouse: "bg-orange-600", Leadership: "bg-zinc-900", Logistics: "bg-teal-600", Production: "bg-amber-600", Procurement: "bg-emerald-600", HR: "bg-pink-600", Support: "bg-slate-600", Other: "bg-zinc-500" };
const LEVEL_BADGE = { TOP: "bg-zinc-200 text-zinc-700", MIDDLE: "bg-blue-50 text-blue-700", BOTTOM: "bg-green-50 text-green-700" };
const FALLBACK_DEPTS = [];

import { useConfig, ALL_CONFIG_GROUPS } from "@/hooks/useConfig";

export default function RolesPage() {
  const { getValues } = useConfig(ALL_CONFIG_GROUPS);
  const DEPARTMENTS = getValues("departments");
  const ROLE_LEVELS = getValues("role_levels");
  const [roles, setRoles] = useState([]);
  const [allPermissions, setAllPermissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [detailRole, setDetailRole] = useState(null);
  const [detailPerms, setDetailPerms] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [editingRole, setEditingRole] = useState(null);
  const [editPerms, setEditPerms] = useState({});
  const [editExpandedGroups, setEditExpandedGroups] = useState({});
  const [saving, setSaving] = useState(false);
  const [renameRole, setRenameRole] = useState(null);
  const [renameName, setRenameName] = useState("");
  const [createForm, setCreateForm] = useState({ name: "", description: "", department: "Sales", role_level: "BOTTOM", clone_from: "", permissions: {} });
  const [expandedGroups, setExpandedGroups] = useState({});

  const load = async () => {
    try {
      const [rolesRes, permsRes] = await Promise.all([api.get("/roles"), api.get("/permissions")]);
      setRoles(rolesRes.data);
      setAllPermissions(permsRes.data);
    } catch (err) { console.error("API get /roles error:", err); } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const systemRoles = roles.filter(r => r.is_system_role && !r.is_placeholder);
  const customRoles = roles.filter(r => !r.is_system_role && !r.is_placeholder && r.is_active !== false);
  const placeholderRoles = roles.filter(r => r.is_placeholder);
  const placeholderByDept = {};
  placeholderRoles.forEach(r => {
    const d = r.department || "Other";
    if (!placeholderByDept[d]) placeholderByDept[d] = [];
    placeholderByDept[d].push(r);
  });

  const permGroups = {};
  allPermissions.forEach(p => {
    const g = p.permission_group || "Other";
    if (!permGroups[g]) permGroups[g] = [];
    permGroups[g].push(p);
  });

  const viewRoleDetail = async (role) => {
    try {
      const { data } = await api.get(`/roles/${role.name}/permissions`);
      setDetailPerms(data);
    } catch { setDetailPerms([]); }
    setDetailRole(role);
  };

  const openEditPermissions = async (role) => {
    try {
      const { data: rolePerms } = await api.get(`/roles/${role.name}/permissions`);
      const permsMap = {};
      allPermissions.forEach(p => { permsMap[p.permission_key] = false; });
      rolePerms.forEach(p => { if (p.allowed) permsMap[p.permission_key] = true; });
      setEditPerms(permsMap);
      setEditingRole(role);
    } catch { toast.error("Failed to load permissions"); }
  };

  const saveEditPermissions = async () => {
    setSaving(true);
    try {
      await api.put(`/roles/${editingRole._id}`, { permissions: editPerms });
      toast.success(`${editingRole.name} permissions updated`);
      setEditingRole(null);
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const toggleEditGroup = (g) => setEditExpandedGroups(prev => ({ ...prev, [g]: prev[g] === false ? true : prev[g] === undefined ? false : !prev[g] }));

  const cloneRole = async (role) => {
    try { await api.post(`/roles/${role._id}/clone`); toast.success(`Cloned ${role.name}`); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const handleRename = async () => {
    if (!renameRole || !renameName.trim()) return;
    try {
      await api.put(`/roles/${renameRole._id}`, { name: renameName.trim() });
      toast.success(`Renamed to "${renameName.trim()}"`);
      setRenameRole(null); setRenameName(""); load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const toggleGroup = (g) => setExpandedGroups(prev => ({ ...prev, [g]: !prev[g] }));

  const initCreate = (cloneFrom = "") => {
    const perms = {};
    allPermissions.forEach(p => { perms[p.permission_key] = false; });
    if (cloneFrom) {
      api.get(`/roles/${cloneFrom}/permissions`).then(({ data }) => {
        data.forEach(p => { if (p.allowed) perms[p.permission_key] = true; });
        setCreateForm(prev => ({ ...prev, permissions: { ...perms } }));
      });
    }
    setCreateForm({ name: "", description: "", department: "Sales", role_level: "BOTTOM", clone_from: cloneFrom, permissions: perms });
    setShowCreate(true);
  };

  const saveCustomRole = async () => {
    try {
      const enabledPerms = {};
      Object.entries(createForm.permissions).forEach(([k, v]) => { if (v) enabledPerms[k] = true; });
      await api.post("/roles", { ...createForm, permissions: enabledPerms });
      toast.success("Custom role created");
      setShowCreate(false); load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  if (loading) return <p className="text-xs text-zinc-400 text-center py-12">Loading...</p>;

  return (
    <div data-testid="roles-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight text-zinc-950">Role Templates</h1>
          <p className="text-sm text-zinc-500 mt-1">{systemRoles.length} system roles, {customRoles.length} custom, {placeholderRoles.length} planned. All permissions are database-driven.</p>
        </div>
        <Button onClick={() => initCreate("")} className="rounded-sm h-8 text-xs" data-testid="create-custom-role-btn"><Plus size={14} /> Create Custom Role</Button>
      </div>

      {/* ═══ SECTION 1: ACTIVE SYSTEM ROLES ═══ */}
      <div className="mb-8">
        <h2 className="font-heading text-lg font-semibold text-zinc-950 mb-3">Active System Roles</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4" data-testid="system-roles-grid">
          {systemRoles.map(role => {
            const Icon = DEPT_ICONS[role.department] || Shield;
            const bgCls = DEPT_COLORS[role.department] || "bg-zinc-500";
            return (
              <div key={role.name} className="border border-zinc-200 p-4" data-testid={`role-card-${role.name.replace(/\s+/g, '-').toLowerCase()}`}>
                <div className="flex items-start justify-between mb-3">
                  <div className={`w-8 h-8 flex items-center justify-center rounded-sm text-white ${bgCls}`}><Icon size={14} /></div>
                  <div className="flex items-center gap-1.5">
                    <Lock size={10} className="text-zinc-400" />
                    <span className="text-[10px] font-mono text-zinc-400">{role.user_count || 0} users</span>
                    {role.role_level && <span className={`px-1.5 py-0.5 text-[8px] font-bold uppercase rounded-sm ${LEVEL_BADGE[role.role_level] || ''}`}>{role.role_level}</span>}
                  </div>
                </div>
                <h3 className="font-heading font-semibold text-sm text-zinc-900">{role.name}</h3>
                <p className="text-[10px] text-zinc-500 mt-0.5">{role.department} &middot; {role.description}</p>
                <div className="flex gap-1.5 mt-3">
                  <Button variant="outline" size="sm" onClick={() => openEditPermissions(role)} className="rounded-sm h-6 text-[10px] px-2" data-testid={`edit-perms-${role.name.replace(/\s+/g,'-').toLowerCase()}`}><Edit2 size={10} /> Edit Permissions</Button>
                  <Button variant="outline" size="sm" onClick={() => viewRoleDetail(role)} className="rounded-sm h-6 text-[10px] px-2" data-testid={`view-perms-${role.name.replace(/\s+/g,'-').toLowerCase()}`}><Eye size={10} /> View</Button>
                  <Button variant="outline" size="sm" onClick={() => cloneRole(role)} className="rounded-sm h-6 text-[10px] px-2"><Copy size={10} /> Clone</Button>
                  {!role.is_system_role && <Button variant="outline" size="sm" onClick={() => { setRenameRole(role); setRenameName(role.name); }} className="rounded-sm h-6 text-[10px] px-2">Rename</Button>}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* ═══ SECTION 2: CUSTOM ROLES ═══ */}
      <div className="mb-8">
        <h2 className="font-heading text-lg font-semibold text-zinc-950 mb-3">Custom Roles</h2>
        {customRoles.length === 0 ? (
          <div className="border border-dashed border-zinc-300 rounded-sm p-6 text-center">
            <p className="text-xs text-zinc-400 mb-2">No custom roles yet.</p>
            <Button variant="outline" onClick={() => initCreate("")} className="rounded-sm text-xs"><Plus size={12} /> Create Custom Role</Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {customRoles.map(role => {
              const Icon = DEPT_ICONS[role.department] || Users;
              const bgCls = DEPT_COLORS[role.department] || "bg-zinc-500";
              return (
                <div key={role.name} className="border border-zinc-200 p-4">
                  <div className="flex items-start justify-between mb-2">
                    <div className={`w-7 h-7 flex items-center justify-center rounded-sm text-white ${bgCls}`}><Icon size={12} /></div>
                    <span className="text-[10px] font-mono text-zinc-400">{role.user_count || 0} users</span>
                  </div>
                  <h3 className="font-heading font-semibold text-sm text-zinc-900">{role.name}</h3>
                  <p className="text-[10px] text-zinc-500">{role.department} &middot; {role.description}</p>
                  <div className="flex gap-1.5 mt-2">
                    <Button variant="outline" size="sm" onClick={() => openEditPermissions(role)} className="rounded-sm h-6 text-[10px] px-2"><Edit2 size={10} /> Edit</Button>
                    <Button variant="outline" size="sm" onClick={() => viewRoleDetail(role)} className="rounded-sm h-6 text-[10px] px-2"><Eye size={10} /> View</Button>
                    <Button variant="outline" size="sm" onClick={() => cloneRole(role)} className="rounded-sm h-6 text-[10px] px-2"><Copy size={10} /> Clone</Button>
                    <Button variant="outline" size="sm" onClick={() => { setRenameRole(role); setRenameName(role.name); }} className="rounded-sm h-6 text-[10px] px-2">Rename</Button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* ═══ SECTION 3: COMING SOON — PLACEHOLDER ROLES ═══ */}
      <div>
        <h2 className="font-heading text-lg font-semibold text-zinc-950 mb-3">Coming Soon — Future Roles</h2>
        <p className="text-xs text-zinc-500 mb-4">These roles activate when their module is built. No code change needed — just set is_active=true and assign permissions.</p>
        {Object.entries(placeholderByDept).map(([dept, deptRoles]) => {
          const Icon = DEPT_ICONS[dept] || Users;
          return (
            <div key={dept} className="mb-4">
              <div className="flex items-center gap-2 mb-2"><Icon size={14} className="text-zinc-400" /><h3 className="text-xs font-semibold uppercase tracking-wider text-zinc-500">{dept}</h3></div>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-2">
                {deptRoles.map(role => (
                  <div key={role.name} className="border border-dashed border-zinc-300 p-3 opacity-60" data-testid={`placeholder-${role.name.replace(/\s+/g,'-').toLowerCase()}`}>
                    <div className="flex items-center gap-1.5 mb-1"><Lock size={10} className="text-zinc-400" /><span className="text-xs font-medium text-zinc-600">{role.name}</span></div>
                    <p className="text-[10px] text-zinc-400 line-clamp-2">{role.description}</p>
                    {role.activates_with && <p className="text-[9px] text-zinc-400 mt-1 italic">Activates with {role.activates_with}</p>}
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>

      {/* ═══ ROLE DETAIL DIALOG ═══ */}
      <Dialog open={!!detailRole} onOpenChange={() => setDetailRole(null)}>
        <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto">
          {detailRole && (
            <>
              <DialogHeader>
                <DialogTitle className="font-heading">{detailRole.name}</DialogTitle>
                <DialogDescription>{detailRole.department} &middot; {detailRole.role_level} &middot; {detailRole.description}</DialogDescription>
              </DialogHeader>
              <div className="space-y-2">
                <p className="text-[10px] font-semibold uppercase text-zinc-400">{detailPerms.length} permissions granted</p>
                {Object.entries(permGroups).map(([group, perms]) => {
                  const grantedInGroup = perms.filter(p => detailPerms.some(dp => dp.permission_key === p.permission_key && dp.allowed));
                  if (grantedInGroup.length === 0) return null;
                  return (
                    <div key={group} className="border border-zinc-200 rounded-sm">
                      <div className="px-3 py-1.5 bg-zinc-50 border-b border-zinc-200 text-[10px] font-semibold uppercase text-zinc-500">{group} ({grantedInGroup.length}/{perms.length})</div>
                      {grantedInGroup.map(p => (
                        <div key={p.permission_key} className="px-3 py-1 border-b border-zinc-50 last:border-0 flex items-center gap-2">
                          <div className="w-1.5 h-1.5 rounded-full bg-green-500 shrink-0" />
                          <div><span className="text-xs text-zinc-700">{p.permission_label}</span><span className="text-[10px] text-zinc-400 ml-2 font-mono">{p.permission_key}</span></div>
                        </div>
                      ))}
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* ═══ CREATE CUSTOM ROLE DIALOG ═══ */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading">Create Custom Role</DialogTitle>
            <DialogDescription>Define a new role with specific permissions. Changes apply immediately.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">Role Name</Label><Input value={createForm.name} onChange={(e) => setCreateForm({...createForm, name: e.target.value})} className="mt-1 rounded-sm" data-testid="custom-role-name" /></div>
              <div>
                <Label className="text-xs">Department</Label>
                <Select value={createForm.department} onValueChange={(v) => setCreateForm({...createForm, department: v})}>
                  <SelectTrigger className="mt-1 rounded-sm"><SelectValue /></SelectTrigger>
                  <SelectContent>{DEPARTMENTS.map(d => <SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">Description</Label><Input value={createForm.description} onChange={(e) => setCreateForm({...createForm, description: e.target.value})} className="mt-1 rounded-sm" /></div>
              <div>
                <Label className="text-xs">Role Level</Label>
                <Select value={createForm.role_level} onValueChange={(v) => setCreateForm({...createForm, role_level: v})}>
                  <SelectTrigger className="mt-1 rounded-sm"><SelectValue /></SelectTrigger>
                  <SelectContent>{ROLE_LEVELS.map(rl => <SelectItem key={rl} value={rl}>{rl.charAt(0) + rl.slice(1).toLowerCase()}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label className="text-xs">Clone Permissions From (optional)</Label>
              <Select value={createForm.clone_from || "none"} onValueChange={(v) => { const cf = v === "none" ? "" : v; initCreate(cf); }}>
                <SelectTrigger className="mt-1 rounded-sm"><SelectValue placeholder="Start blank" /></SelectTrigger>
                <SelectContent><SelectItem value="none">Start blank</SelectItem>{systemRoles.map(r => <SelectItem key={r.name} value={r.name}>{r.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>

            {/* Permissions Checklist */}
            <div>
              <Label className="text-xs font-semibold">Permissions</Label>
              <div className="mt-2 space-y-1">
                {Object.entries(permGroups).map(([group, perms]) => {
                  const enabledCount = perms.filter(p => createForm.permissions[p.permission_key]).length;
                  const isExpanded = expandedGroups[group] !== false;
                  return (
                    <div key={group} className="border border-zinc-200 rounded-sm">
                      <button onClick={() => toggleGroup(group)} className="w-full flex items-center justify-between px-3 py-2 bg-zinc-50 hover:bg-zinc-100 transition-colors">
                        <span className="text-xs font-medium text-zinc-700">{group} <span className="text-zinc-400">({enabledCount}/{perms.length})</span></span>
                        {isExpanded ? <ChevronDown size={12} className="text-zinc-400" /> : <ChevronRight size={12} className="text-zinc-400" />}
                      </button>
                      {isExpanded && (
                        <div className="divide-y divide-zinc-50">
                          {perms.map(p => (
                            <label key={p.permission_key} className="flex items-center justify-between px-3 py-1.5 hover:bg-zinc-50 cursor-pointer">
                              <div>
                                <span className="text-xs text-zinc-700">{p.permission_label}</span>
                                <span className="text-[9px] text-zinc-400 ml-1 font-mono">{p.permission_key}</span>
                              </div>
                              <Switch checked={createForm.permissions[p.permission_key] || false}
                                onCheckedChange={(v) => setCreateForm(prev => ({ ...prev, permissions: { ...prev.permissions, [p.permission_key]: v } }))}
                                className="scale-75" />
                            </label>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreate(false)} className="rounded-sm text-xs">Cancel</Button>
            <Button onClick={saveCustomRole} disabled={!createForm.name} className="rounded-sm text-xs" data-testid="save-custom-role-btn">Save Custom Role</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ═══ EDIT PERMISSIONS DIALOG (works for ALL roles including system) ═══ */}
      <Dialog open={!!editingRole} onOpenChange={() => setEditingRole(null)}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="edit-permissions-dialog">
          {editingRole && (
            <>
              <DialogHeader>
                <DialogTitle className="font-heading">Edit Permissions — {editingRole.name}</DialogTitle>
                <DialogDescription>
                  {editingRole.department} &middot; {editingRole.description}
                  {editingRole.is_system_role && <span className="ml-2 px-1.5 py-0.5 text-[9px] bg-zinc-100 text-zinc-600 rounded-sm">System Role — name locked, permissions editable</span>}
                  {editingRole.user_count > 0 && <span className="ml-2 text-amber-600 text-[10px] font-semibold">Changes affect {editingRole.user_count} active user{editingRole.user_count > 1 ? 's' : ''} immediately</span>}
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-1 mt-2">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs text-zinc-500">{Object.values(editPerms).filter(v => v).length} of {Object.keys(editPerms).length} permissions enabled</span>
                  <div className="flex gap-2">
                    <Button variant="outline" className="h-6 text-[10px] px-2 rounded-sm" onClick={() => { const p = {...editPerms}; Object.keys(p).forEach(k => p[k] = true); setEditPerms(p); }}>Enable All</Button>
                    <Button variant="outline" className="h-6 text-[10px] px-2 rounded-sm" onClick={() => { const p = {...editPerms}; Object.keys(p).forEach(k => p[k] = false); setEditPerms(p); }}>Disable All</Button>
                  </div>
                </div>
                {Object.entries(permGroups).map(([group, perms]) => {
                  const enabledCount = perms.filter(p => editPerms[p.permission_key]).length;
                  const isExpanded = editExpandedGroups[group] !== false;
                  return (
                    <div key={group} className="border border-zinc-200 rounded-sm">
                      <button onClick={() => toggleEditGroup(group)} className="w-full flex items-center justify-between px-3 py-2 bg-zinc-50 hover:bg-zinc-100 transition-colors">
                        <span className="text-xs font-medium text-zinc-700">{group} <span className={`${enabledCount > 0 ? 'text-green-600' : 'text-zinc-400'}`}>({enabledCount}/{perms.length})</span></span>
                        {isExpanded ? <ChevronDown size={12} className="text-zinc-400" /> : <ChevronRight size={12} className="text-zinc-400" />}
                      </button>
                      {isExpanded && (
                        <div className="divide-y divide-zinc-50">
                          {perms.map(p => (
                            <label key={p.permission_key} className="flex items-center justify-between px-3 py-1.5 hover:bg-zinc-50 cursor-pointer">
                              <div className="flex-1 min-w-0 mr-3">
                                <span className="text-xs text-zinc-800 block">{p.permission_label}</span>
                                <span className="text-[9px] text-zinc-400 font-mono">{p.permission_key}</span>
                                {p.description && <span className="text-[9px] text-zinc-400 ml-2">— {p.description}</span>}
                              </div>
                              <Switch checked={editPerms[p.permission_key] || false}
                                onCheckedChange={(v) => setEditPerms(prev => ({ ...prev, [p.permission_key]: v }))}
                                className="scale-75 shrink-0" data-testid={`edit-perm-${p.permission_key}`} />
                            </label>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
              <DialogFooter className="mt-4">
                <Button variant="outline" onClick={() => setEditingRole(null)} className="rounded-sm text-xs">Cancel</Button>
                <Button onClick={saveEditPermissions} disabled={saving} className="rounded-sm text-xs" data-testid="save-edit-perms-btn">
                  {saving ? "Saving..." : "Save Permissions"}
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Rename Dialog */}
      <Dialog open={!!renameRole} onOpenChange={() => setRenameRole(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle className="font-heading text-sm">Rename Role</DialogTitle></DialogHeader>
          <p className="text-[10px] text-zinc-500 mb-2">Current name: <strong>{renameRole?.name}</strong></p>
          <Input value={renameName} onChange={e => setRenameName(e.target.value)} className="h-8 text-xs" placeholder="New role name" data-testid="rename-role-input" />
          <p className="text-[10px] text-amber-600 mt-1">This will update the role name for all users currently assigned to it.</p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRenameRole(null)} className="rounded-sm text-xs">Cancel</Button>
            <Button onClick={handleRename} disabled={!renameName.trim() || renameName.trim() === renameRole?.name} className="rounded-sm text-xs">Rename</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
